/**
 * GENERATED FILE — do not edit by hand.
 * Regenerate with:  npm run fit:dynasty
 *
 * The dynasty model: what an incoming rookie is worth, and how that value ages.
 *
 * Source: nflverse player master and regular-season player totals, seasons
 * 2006-2025 (openly licensed). Fitted 2026-08-01 by scripts/fit-dynasty.ts.
 *
 * See that script's header for the full derivation. In brief:
 *
 *  - `DRAFT_CAPITAL_CURVES` maps an NFL draft pick to expected reference-scored
 *    fantasy points in each of a player's first 5 seasons, averaged over
 *    **every** player drafted there including those who never played. It is an
 *    expectation, so it already carries bust risk.
 *
 *  - `AGE_MULTIPLIERS` is the year-over-year value a player retains at each age,
 *    fitted within-player (each player against himself) so that survivorship
 *    cannot make old players look good. Levels sit below 1.0 at every age
 *    because ordinary regression to the mean is folded in; the signal is the
 *    shape and the gap between positions.
 *
 *  - `ROOKIE_PICK_VALUES` prices the pick itself, normalised so the 1.01 is 1.0,
 *    measured above positional replacement so a quarterback and a running back
 *    are comparable, and ordered ex ante so no hindsight leaks in.
 *
 *  - `REFERENCE_SCORING` is the half-PPR scoring the curves were fitted under.
 *    It is NOT a user's league scoring — it exists so the runtime can invert
 *    expected points back into a positional rank and then re-score the
 *    resulting stat line under whatever the league actually uses (FR-21).
 */

import type { StatCurve } from './positionCurves.ts';

/** Seasons of production the dynasty model was fitted from. */
export const DYNASTY_SEASONS: readonly [number, number] = [2006, 2025];

/** How many seasons of a career `DRAFT_CAPITAL_CURVES` covers. */
export const CAREER_YEARS = 5;

/**
 * Expected reference-scored points as a function of NFL draft pick, per
 * position, per career year (1-indexed). Same Hill form as the redraft curves:
 *
 *     points(pick) = L / (1 + (pick / r0)^k)
 */
export const DRAFT_CAPITAL_CURVES: Record<string, Record<number, StatCurve>> = {
  QB: {
      1: { L: 192.5054, r0: 35.4333, k: 1.55, r2: 0.992 },
      2: { L: 221.7671, r0: 48.5333, k: 1.75, r2: 0.9944 },
      3: { L: 211.1905, r0: 33.25, k: 1.75, r2: 0.9918 },
      4: { L: 178.2161, r0: 41.9833, k: 1.7, r2: 0.9894 },
      5: { L: 157.467, r0: 41.9833, k: 1.65, r2: 0.983 },
  },
  RB: {
      1: { L: 163.3878, r0: 76.9167, k: 1.7, r2: 0.9804 },
      2: { L: 171.1224, r0: 87.8333, k: 1.95, r2: 0.9794 },
      3: { L: 172.0745, r0: 76.9167, k: 1.65, r2: 0.9832 },
      4: { L: 152.7196, r0: 81.2833, k: 2.05, r2: 0.9809 },
      5: { L: 116.9629, r0: 79.1, k: 2.05, r2: 0.9833 },
  },
  WR: {
      1: { L: 129.5108, r0: 59.45, k: 1.6, r2: 0.9885 },
      2: { L: 151.2674, r0: 61.6333, k: 1.35, r2: 0.9738 },
      3: { L: 146.3545, r0: 63.8167, k: 1.5, r2: 0.9629 },
      4: { L: 127.1144, r0: 66, k: 1.6, r2: 0.9743 },
      5: { L: 130.7437, r0: 50.7167, k: 1.35, r2: 0.9627 },
  },
  TE: {
      1: { L: 91.6169, r0: 61.6333, k: 1.9, r2: 0.9733 },
      2: { L: 97.6959, r0: 83.4667, k: 2.15, r2: 0.9721 },
      3: { L: 89.3079, r0: 96.5667, k: 2.05, r2: 0.9624 },
      4: { L: 84.0599, r0: 100.9333, k: 2.2, r2: 0.9544 },
      5: { L: 81.8461, r0: 83.4667, k: 1.95, r2: 0.9655 },
  },
};

/** Year-over-year value retained at each age, by position. */
export const AGE_MULTIPLIERS: Record<string, Record<number, number>> = {
  QB: { 22: 1.1405, 23: 0.8414, 24: 0.822, 25: 0.6412, 26: 0.7796, 27: 0.8398, 28: 0.7943, 29: 0.7498, 30: 0.7701, 31: 0.8903, 32: 0.6056, 33: 0.7809, 34: 0.7394, 36: 0.676 },
  RB: { 21: 1.0971, 22: 0.8366, 23: 0.8741, 24: 0.8005, 25: 0.7474, 26: 0.6432, 27: 0.7026, 28: 0.6677, 29: 0.5949, 30: 0.6736, 31: 0.4647, 32: 0.3794 },
  WR: { 21: 1.0798, 22: 0.9868, 23: 0.7731, 24: 0.8378, 25: 0.7607, 26: 0.77, 27: 0.7456, 28: 0.6876, 29: 0.7032, 30: 0.6174, 31: 0.5913, 32: 0.6399, 33: 0.5211, 34: 0.362 },
  TE: { 22: 0.9349, 23: 0.9087, 24: 0.9218, 25: 0.769, 26: 0.753, 27: 0.8619, 28: 0.7777, 29: 0.6313, 30: 0.5482, 31: 0.6552 },
};

/**
 * Dynasty rookie pick value by overall slot (index 0 = the 1.01), normalised to
 * the 1.01. Covers 48 slots — four rounds of a 12-team rookie draft.
 */
export const ROOKIE_PICK_VALUES: readonly number[] = [
  1.0000, 0.6799, 0.5278, 0.4366, 0.3750, 0.3302, 0.2961, 0.2690,
  0.2470, 0.2287, 0.2132, 0.1999, 0.1884, 0.1782, 0.1693, 0.1612,
  0.1540, 0.1475, 0.1416, 0.1362, 0.1312, 0.1267, 0.1224, 0.1185,
  0.1149, 0.1115, 0.1083, 0.1053, 0.1025, 0.0998, 0.0973, 0.0950,
  0.0928, 0.0906, 0.0886, 0.0867, 0.0849, 0.0832, 0.0815, 0.0799,
  0.0784, 0.0769, 0.0755, 0.0742, 0.0729, 0.0717, 0.0705, 0.0693,
];

/**
 * The same chart in absolute terms: mean reference-scored points produced over
 * the player's first 5 seasons. Kept so the UI can show a real quantity
 * rather than only a ratio.
 */
export const ROOKIE_PICK_POINTS: readonly number[] = [
  193.9, 131.9, 102.4, 84.7, 72.7, 64.0, 57.4, 52.2,
  47.9, 44.4, 41.3, 38.8, 36.5, 34.6, 32.8, 31.3,
  29.9, 28.6, 27.5, 26.4, 25.4, 24.6, 23.7, 23.0,
  22.3, 21.6, 21.0, 20.4, 19.9, 19.4, 18.9, 18.4,
  18.0, 17.6, 17.2, 16.8, 16.5, 16.1, 15.8, 15.5,
  15.2, 14.9, 14.6, 14.4, 14.1, 13.9, 13.7, 13.4,
];

/** Half-PPR scoring the curves were fitted under. Not league scoring. */
export const REFERENCE_SCORING: Record<string, number> = {
  "pass_yd": 0.04,
  "pass_td": 4,
  "pass_int": -2,
  "pass_2pt": 2,
  "rush_yd": 0.1,
  "rush_td": 6,
  "rush_2pt": 2,
  "rec": 0.5,
  "rec_yd": 0.1,
  "rec_td": 6,
  "rec_2pt": 2,
  "fum_lost": -2,
  "st_td": 6,
};
